/**
 * Montage de l'exploration (ADR 0013 §4-6, epic 3) : construit et pilote le
 * trio `ExploreState`/`ExploreView` + `ObjectiveHud`/`BriefLineView` pour une
 * scene `explore` du chapitre -- monde 3D, boucle d'image, clavier, souris.
 *
 * Extrait de `chapter.ts` (revue de fin d'epic 3, "assez carre pour s'etendre
 * facilement") : `ChapterApp` etait devenu le chef d'orchestre de tout a la
 * fois -- routeur de scenes, dialogues, tirage, combat, sauvegarde -- ET tout
 * le montage de l'exploration. Une bonne trentaine de ses membres ne
 * parlaient QUE d'exploration (`enterExploreScene`, `buildExploreWorld`,
 * `startExploreLoop`/`stopExploreLoop`, `attachExploreKeyboard`/
 * `detachExploreKeyboard`, `wireExploreCanvasInput`, `handleExploreHover`,
 * `syncExploreVisibility`, `syncExploreFollowerRigs`, `centerCameraOnLeader`,
 * `exploreAspect`, les quatre gestionnaires de touches, `exploreHeldKeys`...)
 * -- ce fichier les regroupe.
 *
 * Respecte la meme frontiere que `chapter.ts`/`app.ts` (voir
 * docs/process/ARCHITECTURE.md) : c'est un module de MONTAGE, il a donc le
 * droit de toucher `three` et le DOM (contrairement a `src/explore`, qui
 * reste pur, lui). Ce qu'il NE possede PAS : le routage entre scenes --
 * avancer le `SceneRouter`, ouvrir une conversation annexe via
 * `NarrativeView`, la coupure "CONTACT" avant le combat. Ces decisions
 * restent chez `ChapterApp`, qui reste "celui qui enchaine les scenes et
 * decide qui a la main" -- il les pilote a partir de deux rappels
 * (`ExploreSessionCallbacks`) : `getContext()` (le contexte narratif le plus
 * a jour, lu a chaque frame, jamais mis en cache ici) et `onEvent()` (un
 * evenement d'exploration survenu pendant `tick()`, a router). Le reste --
 * ouvrir/fermer une porte, jouer une bulle, faire face a une entite,
 * rafraichir l'encart d'objectif -- est expose en methodes simples que
 * `ChapterApp` appelle depuis sa propre logique de routage
 * (`handleExploreInteraction`).
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { createRng } from '@/core/rng';
import { loadSession } from '@/core/save';
import { getCharacter } from '@/rules/character';
import { discoveredRoomIdsForMap } from '@/narrative';
import type { FollowerId, GaugeDef, NarrativeContext, SceneDef } from '@/narrative';
import { ExploreState, roomAt } from '@/explore';
import type { Cell, EntityDef, ExploreEvent, InteractableInfo, MapDef } from '@/explore';
import { ExploreView, KEY_ZOOM_SPEED } from '@/render/exploreView';
import type { HoverTarget } from '@/render/exploreView';
import type { NightMood } from '@/render/exploration/atmosphere';
import { createGameRenderer, rendererDescription } from '@/render/rendererSetup';
import { createDormitoryEnvironment } from '@/render/exploration/dormitoryEnvironment';
import { explorationSceneProfile } from '@/render/exploration/explorationSceneProfiles';
import { createStableSceneLights, type StableSceneLights } from '@/render/exploration/stableSceneLights';
import { TAP_SLOP_PX } from '@/render/pointerGestures';
import { Sfx } from '@/audio/sfx';
import type { SfxId } from '@/audio/sfx';
import { CHAPTERS } from '@/data/chapters';
import { HOLT_NUIT_VISUALS } from '@/data/exploreVisuals/holtNuit';
import { CAMPEMENT_VISUALS } from '@/data/exploreVisuals/campement';
import { CONDUITS_VISUALS } from '@/data/exploreVisuals/conduits';
import type { ExploreVisualMapDef } from '@/data/exploreVisualTypes';

/**
 * Habillages des cartes du chapitre 2, passes a `ExploreView` par `art.visuals` (voir
 * `buildWorld`) : `ExploreView.visualDefinition` ne connait en dur que `holt` et
 * `centre-examen`. Une carte du chapitre 2 absente d'ici se rendrait sans aucun meuble.
 */
const CHAPTER_2_VISUALS: Record<string, ExploreVisualMapDef> = {
  [HOLT_NUIT_VISUALS.mapId]: HOLT_NUIT_VISUALS,
  [CONDUITS_VISUALS.mapId]: CONDUITS_VISUALS,
  [CAMPEMENT_VISUALS.mapId]: CAMPEMENT_VISUALS,
};
const DORMITORY_PILOT_EXPOSURE = 1.2;
import { ObjectiveHud } from './ui/objectiveHud';
import type { GaugeStatus } from './ui/gaugeView';
import { BriefLineView } from './ui/briefLine';

/**
 * Climat de `holt-nuit` pour l'etape courante (ADR 0026, lot 5.8b) : les valeurs `Ch2Etape`
 * ('bal'/'fuite') sont lues comme de simples chaines (voir `SceneDef.etape`, generalise par
 * l'ADR 0021) -- ce module n'importe jamais l'union `Ch2Etape` elle-meme, qui appartiendrait au
 * chapitre 1 s'il jouait un jour sur cette carte. Toute autre valeur (une etape du chapitre 1,
 * ou hors etape) retombe sur `null`, le calibrage "academie" par defaut.
 */
function nightMoodForEtape(etape: string | undefined): NightMood {
  if (etape === 'bal') return 'bal';
  if (etape === 'fuite') return 'fuite';
  if (etape === 'conduits') return 'conduits';
  if (etape === 'cantine') return 'cantine';
  if (etape === 'campement') return 'campement';
  return null;
}

/** Touches maintenues du panoramique/zoom continu en exploration (08-EXPLORATION.md "Contrôles"). */
interface ExploreHeldKeys {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  zoomIn: boolean;
  zoomOut: boolean;
}

/** Flèches -> axe de panoramique continu (08-EXPLORATION.md "Contrôles"). */
const EXPLORE_PAN_KEYS: Record<string, keyof ExploreHeldKeys> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

/** Ce que `ChapterApp` doit fournir a la session (voir le commentaire d'en-tete). */
export interface ExploreSessionCallbacks {
  /** Contexte narratif le plus a jour (drapeaux, dont `ch1.etape`) -- lu a chaque frame. */
  getContext(): NarrativeContext;
  /**
   * Relaye un evenement survenu pendant `tick()` (ou une interaction de debug) au chapitre,
   * seul a savoir router une entite qui termine l'objectif, ouvrir une conversation annexe, ou
   * persister une piece decouverte (voir `ChapterApp.handleExploreEvent`).
   */
  onEvent(ev: ExploreEvent): void;
  /** Lieu courant, y compris une pièce déjà découverte visitée de nouveau. */
  onLocationChange?(mapId: string, roomId: string | undefined): void;
}

/** Compteurs WebGL de la dernière image d'exploration, ombres incluses. */
export interface ExploreRenderStats {
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
  /**
   * Nom du GPU tel que le pilote le rapporte, `null` si le navigateur masque l'extension. La
   * question qu'il répond : DEUX navigateurs sur la même machine peuvent choisir deux cartes
   * différentes (défaut réel : fluide sous Edge, poussif sous Chrome, même build). Sans ce
   * champ, on ne peut pas distinguer « le jeu est trop lourd » de « ce navigateur rend sur la
   * carte intégrée, ou en logiciel ».
   */
  gpu: string | null;
  /** Rapport de pixels effectif (voir `MAX_PIXEL_RATIO`) : l'autre moitié du coût par image. */
  pixelRatio: number;
}

export class ExploreSession {
  /**
   * Etat + rendu de l'exploration : construits UNE FOIS (comme `tacticalApp` dans
   * `ChapterApp`) et reutilises d'une etape a l'autre tant que la carte ne change pas --
   * c'est ce qui garantit qu'on ne teleporte jamais Franklyn entre deux etapes
   * d'exploration qui se suivent sur la meme carte (contrat du lot 3.6b : "le spawn ne
   * sert qu'a une entree a froid"). Publics (comme `GameApp.combat`) : `ChapterApp` les lit
   * directement pour son propre routage (`handleExploreInteraction`, l'API de debug...).
   */
  state: ExploreState | null = null;
  view: ExploreView | null = null;
  private stableLights: StableSceneLights | null = null;
  mapDef: MapDef | null = null;

  private renderer: THREE.WebGLRenderer | null = null;
  /** Compositeur AAA du pilote, activé uniquement sur HOLT et HOLT-nuit. */
  private dormitoryComposer: EffectComposer | null = null;
  private dormitoryRenderPass: RenderPass | null = null;
  /** Préfiltrage partagé jour/nuit ; les vues empruntent sa texture, la session la détruit. */
  private dormitoryEnvironment: THREE.WebGLRenderTarget | null = null;
  private canvas: HTMLCanvasElement | null = null;
  /** HUD/bulles : recrees a chaque entree (voir `resume`/`pause`) pour ne jamais laisser un ecouteur clavier global actif pendant un dialogue ou le combat. */
  private hud: ObjectiveHud | null = null;
  private briefLine: BriefLineView | null = null;
  private hoveredEntityId: string | null = null;
  private hoveredCharacterName: string | null = null;
  private lastPointerClient = { x: 0, y: 0 };
  private followerRigIds: string[] = [];
  /** Bruitages d'exploration (ADR 0024 §2, ex. les repliques de pression). */
  private readonly sfx = new Sfx(true, loadSession().soundMuted);
  private audioLocationKey = '';
  private readonly heldKeys: ExploreHeldKeys = {
    up: false,
    down: false,
    left: false,
    right: false,
    zoomIn: false,
    zoomOut: false,
  };
  /** Jeton de generation de la boucle d'image (voir `startLoop`) : invalide toute frame en vol des qu'on arrete/redemarre. */
  private loopId = 0;
  private rafId: number | null = null;
  private lastFrameTime = 0;
  /** `true` tant que les ecouteurs clavier d'exploration sont attaches (voir `attachKeyboard`/`detachKeyboard`). */
  private keyboardAttached = false;

  renderStats(): ExploreRenderStats | null {
    if (!this.renderer || !this.view) return null;
    const { render, memory } = this.renderer.info;
    return {
      gpu: rendererDescription(this.renderer),
      pixelRatio: this.renderer.getPixelRatio(),
      drawCalls: render.calls,
      triangles: render.triangles,
      geometries: memory.geometries,
      textures: memory.textures,
    };
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    this.sfx.unlock();
    const panKey = EXPLORE_PAN_KEYS[e.key];
    if (panKey) {
      this.heldKeys[panKey] = true;
      e.preventDefault();
      return;
    }
    if (e.key === '+' || e.key === '=') {
      this.heldKeys.zoomIn = true;
      return;
    }
    if (e.key === '-' || e.key === '_') {
      this.heldKeys.zoomOut = true;
      return;
    }
    if (e.key === 'a' || e.key === 'A') this.rotateCamera(-1);
    else if (e.key === 'e' || e.key === 'E') this.rotateCamera(1);
    else if (e.key === 'c' || e.key === 'C') this.centerCameraOnLeader();
    else if ((e.key === ' ' || e.code === 'Space') && !this.hud?.hasSelection() && this.hoveredEntityId) {
      e.preventDefault();
      this.requestInteract(this.hoveredEntityId);
    }
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    const panKey = EXPLORE_PAN_KEYS[e.key];
    if (panKey) this.heldKeys[panKey] = false;
    else if (e.key === '+' || e.key === '=') this.heldKeys.zoomIn = false;
    else if (e.key === '-' || e.key === '_') this.heldKeys.zoomOut = false;
  };

  /** Touches restees "enfoncees" si la fenetre perd le focus pendant un appui (alt-tab...). */
  private readonly onBlur = (): void => {
    this.heldKeys.up = this.heldKeys.down = this.heldKeys.left = this.heldKeys.right = false;
    this.heldKeys.zoomIn = this.heldKeys.zoomOut = false;
  };

  private readonly onResize = (): void => {
    if (!this.renderer || !this.view) return;
    this.renderer.setSize(this.host.clientWidth, this.host.clientHeight, false);
    this.dormitoryComposer?.setSize(this.host.clientWidth, this.host.clientHeight);
    this.view.resize(this.aspect(), this.host.clientWidth, this.host.clientHeight);
  };

  private disposeDormitoryComposer(): void {
    if (!this.dormitoryComposer) return;
    // Composer targets and each pass's private material/targets have separate owners.
    for (const pass of this.dormitoryComposer.passes) {
      const ownedPass = pass as typeof pass & { dispose?: () => void };
      ownedPass.dispose?.();
    }
    this.dormitoryComposer.dispose();
    this.dormitoryComposer = null;
    this.dormitoryRenderPass = null;
  }

  constructor(
    private readonly host: HTMLElement,
    private readonly callbacks: ExploreSessionCallbacks,
  ) {
    this.host.addEventListener('pointerdown', this.onPointerUnlock);
  }

  private readonly onPointerUnlock = (): void => this.sfx.unlock();

  setSoundMuted(muted: boolean): void {
    this.sfx.setMuted(muted);
  }

  /* ---------------------------------- entree/sortie d'une etape ---------------------------------- */

  /**
   * Entree dans une etape d'exploration : construit (ou reutilise, si la carte n'a pas
   * change) le monde 3D, puis met a jour objectif/coequipiers/repere. Ne recentre PAS la
   * camera elle-meme (voir `centerCameraOnLeader`, appele par `ChapterApp` une fois
   * `exploreHost` rendu visible) et ne demarre pas la boucle d'image (voir `resume`) --
   * les deux restent a la charge de l'appelant, exactement comme avant l'extraction.
   */
  enterStep(mapDef: MapDef, ctx: NarrativeContext, scene: SceneDef, followerIds: FollowerId[]): void {
    if (!this.state || !this.view || this.state.map.id !== mapDef.id) {
      this.buildWorld(mapDef, ctx, scene, followerIds);
    } else {
      // Meme carte que l'etape precedente : on NE reconstruit PAS l'etat (donc on ne
      // teleporte pas Franklyn, voir SceneDef.spawn) -- seul le contexte change.
      this.state.updateContext(ctx);
    }

    this.state?.setFollowers(followerIds);
    this.syncFollowerRigs(followerIds);
    this.state?.setObjective(scene.objective ?? null);
    this.objectiveTriggerId = scene.objective?.completionTrigger ?? null;
    this.view?.setPingTarget(this.objectiveTargetCell(scene));
    // Habillage/climat par etape (ADR 0026, lot 5.8b) : `scene.etape` filtre les placements qui
    // en portent un (`ExploreVisualPlacement.etape`, ex. le buffet du bal contre les pupitres
    // renverses de la fuite, memes cases de `holt-nuit`) -- sans effet sur `holt`/`centre-examen`,
    // dont aucun placement ne porte `etape`. Le climat lumineux (`setNightMood`), lui, reste
    // reserve a `holt-nuit` : seule carte a jouer deux etapes tres differentes (bal/fuite) sur le
    // MEME batiment institutionnel -- `holt`/`centre-examen` gardent leur climat fixe par carte
    // (`EXPLORE_VISUALS`, `exploreVisualsFor`).
    this.view?.setEtape(scene.etape);
    this.view?.setNightMood(mapDef.id in CHAPTER_2_VISUALS ? nightMoodForEtape(scene.etape) : null);
    this.syncVisibility();
    this.hud?.setGauge(this.gaugeStatusFor(ctx));
    this.hud?.setLuck(ctx.run.luck);
    this.updateAudioLocation();
  }

  private updateAudioLocation(): void {
    if (!this.state) return;
    const map = this.state.map.def;
    const roomId = roomAt(map, this.state.leaderCell())?.id;
    const key = `${map.id}:${roomId ?? ''}`;
    if (key === this.audioLocationKey) return;
    this.audioLocationKey = key;
    this.callbacks.onLocationChange?.(map.id, roomId);
  }

  /**
   * Jauge d'etat visible du chapitre courant (ADR 0025 §1, B18 -- reporte du lot 5.4,
   * cable ici au lot 5.8 : "`ObjectiveHud.setGauge` est pret, `exploreSession.ts` ne
   * l'appelle pas"). Recopie volontairement le calcul prive de `ChapterApp.resolveGaugeStatus`/
   * `gaugeStatusFor` (`src/chapter.ts`) plutot que de l'exporter depuis lui : ce module ne
   * recoit du chapitre que `NarrativeContext` (voir `ExploreSessionCallbacks.getContext`),
   * jamais le `ChapterDef` lui-meme -- l'ajouter aurait touche `chapter.ts`, hors de la liste
   * "Toucher" de ce lot. Les deux copies restent petites (moins de 15 lignes) et lisent la
   * MEME donnee (`ChapterDef.gauges`, `GaugeDef.from`) : rien a garder synchronise a la main.
   */
  private gaugeStatusFor(ctx: NarrativeContext): GaugeStatus | null {
    const chapterDef = CHAPTERS[ctx.run.chapter];
    const gauges = chapterDef?.gauges;
    if (!gauges || gauges.length === 0) return null;
    const scenes = chapterDef.scenes;
    const currentIndex = scenes.findIndex((s) => s.id === ctx.run.sceneId);

    for (const gauge of gauges) {
      if (gauge.from) {
        const fromIndex = scenes.findIndex((s) => s.id === gauge.from);
        if (fromIndex === -1 || currentIndex === -1 || currentIndex < fromIndex) continue;
      }
      return this.gaugeStatusFromDef(gauge, ctx);
    }
    return null;
  }

  private gaugeStatusFromDef(gauge: GaugeDef, ctx: NarrativeContext): GaugeStatus {
    const raw = ctx.run.flags[gauge.counter];
    const value = typeof raw === 'number' ? Math.max(0, raw) : 0;
    const levelIndex = Math.min(value, gauge.levels.length - 1);
    return {
      id: gauge.id,
      label: gauge.label,
      levelIndex,
      levelLabel: gauge.levels[levelIndex] ?? gauge.levels[0] ?? '',
      levelsCount: gauge.levels.length,
    };
  }

  /**
   * Construit l'etat/le rendu d'exploration pour `mapDef` -- une entree a froid (nouvelle
   * partie, reprise de sauvegarde, `?scene=`) ou un changement de carte (hors perimetre du
   * chapitre 1, voir le centre d'examen, lot 3.7). `scene.spawn` ne sert QU'ICI : voir
   * `SceneDef.spawn`.
   *
   * `followerIds` doit etre la liste EFFECTIVEMENT rendue (deja bornee a
   * `VISIBLE_FOLLOWERS_LIMIT` par `ChapterApp.exploreFollowers`, jamais recalculee ici) :
   * elle sert a amorcer `ExploreState.seedTrail` (le segment synthetique "avant le spawn",
   * voir sa docstring) avec le BON nombre de suiveurs. Passer un nombre errone (l'ancien
   * code appelait `exploreFollowerIds(ctx.run)`, une regle propre au chapitre 1, qui renvoie
   * `[]` hors de son tirage) amorce un segment trop court : au premier calcul de position
   * (`followerPosition`), chaque suiveur dont le decalage (`lag`) depasse ce segment retombe
   * sur le meme point de depart -- les suiveurs "en trop" se superposent alors exactement,
   * silhouette masquant silhouette (bug reel constate au lot 5.8b : deux suiveurs declares,
   * un seul visible a l'ecran, confirme par `window.__game.explore().followers`, deux
   * entrees identiques).
   */
  private buildWorld(
    mapDef: MapDef,
    ctx: NarrativeContext,
    scene: SceneDef,
    followerIds: FollowerId[],
  ): void {
    this.stableLights?.dispose();
    this.stableLights = null;
    this.view?.dispose();
    const isEnhancedMap = explorationSceneProfile(mapDef.id) !== null;
    if (!isEnhancedMap) this.disposeDormitoryComposer();
    this.followerRigIds = [];
    this.mapDef = mapDef;
    this.state = new ExploreState(mapDef, ctx, {
      spawn: scene.spawn,
      followerIds,
      discoveredRooms: discoveredRoomIdsForMap(ctx.run, mapDef.id),
    });

    if (!this.renderer || !this.canvas) {
      this.canvas = document.createElement('canvas');
      this.host.appendChild(this.canvas);
      this.renderer = createGameRenderer({ canvas: this.canvas, shadows: true, toneMappingExposure: 1.08 });
      // Three remet ses compteurs à zéro APRÈS le rendu des ombres par défaut.
      // Une remise à zéro par image conserve le coût complet, sans changer le renderer partagé.
      this.renderer.info.autoReset = false;
      this.wireCanvasInput(this.canvas);
    }

    const rng = createRng(`${ctx.run.seed}::explore::${mapDef.id}`);
    this.view = new ExploreView(
      mapDef,
      rng,
      this.aspect(),
      {
        onHover: (target) => this.handleHover(target),
        onMoveTo: (cell) => {
          const res = this.state?.walkLeaderTo(cell);
          if (res && !res.ok) console.warn(`ChapterApp (exploration) : deplacement refuse (${res.reason}).`);
        },
        onInteract: (entityId) => this.requestInteract(entityId),
      },
      // `holt-nuit` (lot 5.8) : `ExploreView.visualDefinition` ne connaît en dur que `holt` et
      // `centre-examen` (ADR 0024 §4, réglages généralisés en registre -- mais le CHOIX du
      // placement de mobilier lui-même est resté câblé sur ces deux id, hors de la liste
      // "Toucher" de ce lot). Sans cet override explicite, la variante de nuit se rendrait
      // sans aucun meuble (repli `{ mapId, placements: [] }`) -- `art.visuals` est le point
      // d'extension déjà prévu pour ce cas (voir `src/dev/dormitoryPilotMap.ts`).
      // Lot 5.10 : meme override pour `campement` (`CHAPTER_2_VISUALS`).
      CHAPTER_2_VISUALS[mapDef.id] ? { visuals: CHAPTER_2_VISUALS[mapDef.id] } : {},
    );
    if (isEnhancedMap) {
      if (!this.dormitoryComposer) {
        this.dormitoryComposer = new EffectComposer(this.renderer!);
        this.dormitoryRenderPass = new RenderPass(this.view.scene, this.view.renderCamera);
        this.dormitoryComposer.addPass(this.dormitoryRenderPass);
        this.dormitoryComposer.addPass(new UnrealBloomPass(new THREE.Vector2(800, 450), 0.19, 0.5, 1.6));
        this.dormitoryComposer.addPass(new OutputPass());
      } else if (this.dormitoryRenderPass) {
        this.dormitoryRenderPass.scene = this.view.scene;
        this.dormitoryRenderPass.camera = this.view.renderCamera;
      }
    }
    if (isEnhancedMap) {
      this.dormitoryEnvironment ??= createDormitoryEnvironment(this.renderer);
      this.view.setDormitoryEnvironment(this.dormitoryEnvironment.texture);
      this.view.initializeDormitoryPilotLighting(this.host.clientWidth, this.host.clientHeight);
    }
    // La préférence système concerne les animations de présentation (caméra, repère,
    // poses d'attente), jamais l'avancée de `ExploreState` ni sa vitesse de déplacement.
    this.view.setReducedMotion(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.view.setLeader(getCharacter('franklyn'));
    this.view.updateRigPosition('leader', this.state.leaderCell(), false, 0);
    this.view.centerOn(this.state.leaderCell());
    this.renderer.setSize(this.host.clientWidth, this.host.clientHeight, false);
    this.dormitoryComposer?.setSize(this.host.clientWidth, this.host.clientHeight);
    this.view.resize(this.aspect(), this.host.clientWidth, this.host.clientHeight);
    // Avant la première frame : évite un flash "tout caché" (la vue part pessimiste, voir
    // `ExploreView.buildRoomFloors`/`registerVisualEntity`) le temps que la boucle démarre.
    this.syncVisibility();
    this.stableLights = createStableSceneLights(this.view.scene);
    this.stableLights.update(this.view.camera.getTarget());
    // Compile hidden furniture too, using the same output as the actual render pass.
    const previousTarget = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.dormitoryComposer?.readBuffer ?? null);
    try {
      // Submit shader work ahead of discovery; no uncancellable readiness polling survives
      // a disposed world (Three's compileAsync polls material properties internally).
      this.renderer.compile(this.view.scene, this.view.renderCamera);
    } finally {
      this.renderer.setRenderTarget(previousTarget);
    }
  }

  /** Case cible du repere "Tab maintenu" (08-EXPLORATION.md "Les objectifs") : celle du `completionTrigger`. */
  /**
   * Entite qui termine l'etape courante (`completionTrigger`) : porte la balise permanente de
   * l'objectif, reposee a chaque `syncVisibility` parce que la balise ne se montre que lorsque
   * l'entite elle-meme est visible -- une piece non decouverte ne doit rien laisser fuiter
   * (08-EXPLORATION.md "La decouverte des lieux"). Le repere "Tab maintenu", lui, reste la
   * reponse a la demande quand la salle suivante est encore fermee.
   */
  private objectiveTriggerId: string | null = null;

  private objectiveTargetCell(scene: SceneDef): Cell | null {
    const triggerId = scene.objective?.completionTrigger;
    if (!triggerId) return null;
    return this.entity(triggerId)?.cell ?? null;
  }

  /**
   * Recentre la camera sur la position RÉELLE du meneur (`ExploreState.leaderCell()`),
   * jamais sur `ExploreView.centerOnLeader()` -- ce dernier lit une case mise en cache par
   * `updateRigPosition()`, alimentée uniquement pendant le rendu (boucle d'image en cours).
   * Aux moments où `ChapterApp` demande un recentrage (début d'étape, sortie d'un
   * dialogue, touche `C`), la boucle vient justement d'être à l'arrêt (ou n'a pas encore
   * tourné une seule fois pour cette étape) : la case mise en cache est alors nulle ou
   * périmée d'une étape entière -- défaut réel constaté en vérification visuelle du lot
   * 3.6b (la caméra ne bougeait pas d'une étape à l'autre).
   */
  /** Même quart de tour depuis les touches A/E et le bouton d'exploration. */
  rotateCamera(step: number): void {
    this.view?.rotate(step);
  }

  centerCameraOnLeader(): void {
    if (!this.state || !this.view) return;
    this.view.centerOn(this.state.leaderCell());
  }

  /* ---------------------------------- cycle de vie (montre/masque encart + bulles) ---------------------------------- */

  /**
   * Affiche l'encart d'objectif + les bulles et (re)demarre la boucle d'image. Recree
   * `ObjectiveHud`/`BriefLineView` a chaque entree plutot que de les garder en vie tout le
   * chapitre (contrairement a `state`/`view`) : les deux posent des ecouteurs clavier
   * globaux (Tab, Espace) qu'on ne veut JAMAIS actifs pendant un dialogue ou le combat --
   * les recreer est plus sur que de leur ajouter une API de pause. Appelee par
   * `ChapterApp.setActiveHost`.
   */
  resume(): void {
    if (!this.state || !this.view) return;
    if (!this.hud) {
      this.hud = new ObjectiveHud(this.host, {
        onPingChange: (active) => this.view?.setPingActive(active),
        onInteractSelected: (entityId) => this.requestInteract(entityId),
      });
    }
    this.hud.setObjective(this.state.objectiveStatus());
    this.hud.setGauge(this.gaugeStatusFor(this.callbacks.getContext()));
    this.hud.setLuck(this.callbacks.getContext().run.luck);
    if (!this.briefLine) this.briefLine = new BriefLineView(this.host);
    this.attachKeyboard();
    this.startLoop();
  }

  pause(): void {
    this.stopLoop();
    this.detachKeyboard();
    this.view?.clearHover();
    this.hud?.dispose();
    this.hud = null;
    this.briefLine?.dispose();
    this.briefLine = null;
  }

  /**
   * Reinitialise completement le monde explore (pas seulement une pause) : "Nouvelle
   * partie" (`ChapterApp.startNewGame`) ne doit pas heriter de la position de Franklyn ni
   * du decor seede sur l'ancienne graine -- `enterStep` en reconstruira un neuf des la
   * prochaine etape `explore` (voir `buildWorld`). Le renderer/canvas, eux, persistent
   * (reutilises par `buildWorld` au prochain appel), exactement comme avant l'extraction.
   */
  resetWorld(): void {
    this.pause();
    this.stableLights?.dispose();
    this.stableLights = null;
    this.view?.dispose();
    this.disposeDormitoryComposer();
    this.view = null;
    this.state = null;
    this.mapDef = null;
  }

  dispose(): void {
    this.pause();
    this.host.removeEventListener('pointerdown', this.onPointerUnlock);
    this.sfx.dispose();
    this.stableLights?.dispose();
    this.stableLights = null;
    this.view?.dispose();
    this.disposeDormitoryComposer();
    this.dormitoryEnvironment?.dispose();
    this.dormitoryEnvironment = null;
    this.renderer?.dispose();
    window.removeEventListener('resize', this.onResize);
  }

  private attachKeyboard(): void {
    if (this.keyboardAttached) return;
    this.keyboardAttached = true;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  private detachKeyboard(): void {
    if (!this.keyboardAttached) return;
    this.keyboardAttached = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.heldKeys.up = this.heldKeys.down = this.heldKeys.left = this.heldKeys.right = false;
    this.heldKeys.zoomIn = this.heldKeys.zoomOut = false;
  }

  /**
   * Pointeur : survol, glissé, tapotement, molette et pincement -- câblés une seule fois sur le
   * canvas (persiste tout le chapitre).
   *
   * Un seul geste sert à DEUX choses, et c'est tout le sujet : le doigt (ou la souris) qui se
   * pose et se relève sans bouger est un ORDRE DE DÉPLACEMENT ; le même qui traîne est un
   * PANORAMIQUE. On ne peut donc pas écouter `click` -- il arrive aussi au bout d'un glissé, et
   * la carte partait se recentrer sur la case qu'on venait de faire défiler. On suit les
   * pointeurs nous-mêmes, et le tapotement n'est reconnu qu'au relâchement, si le pointeur a
   * moins bougé que `TAP_SLOP_PX`.
   *
   * Deux pointeurs = pincement (zoom). Sur tablette, ce sont les SEULS gestes disponibles :
   * flèches et molette n'existent pas, la carte doit se conduire entièrement au doigt.
   */
  private wireCanvasInput(canvas: HTMLCanvasElement): void {
    const ndcFrom = (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((clientX - rect.left) / rect.width) * 2 - 1,
        y: -(((clientY - rect.top) / rect.height) * 2 - 1),
      };
    };

    /** Pointeurs actuellement posés sur le canvas, par identifiant, à leur dernière position. */
    const down = new Map<number, { x: number; y: number }>();
    /** Distance parcourue par le geste en cours, en pixels : au-delà du seuil, ce n'est plus un tapotement. */
    let travelPx = 0;
    /** Écartement des deux doigts à la dernière image, en pixels ; 0 = pas de pincement en cours. */
    let pinchPx = 0;

    const pinchSpan = (): { distance: number; cx: number; cy: number } | null => {
      const [a, b] = [...down.values()];
      if (!a || !b) return null;
      return { distance: Math.hypot(b.x - a.x, b.y - a.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    };

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      down.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (down.size === 1) travelPx = 0;
      if (down.size === 2) {
        this.view?.clearHover();
        // Un pincement n'est jamais un tapotement : on le disqualifie tout de suite, sinon
        // relever le deuxième doigt enverrait le meneur marcher là où on voulait zoomer.
        travelPx = Number.POSITIVE_INFINITY;
        pinchPx = pinchSpan()?.distance ?? 0;
      }
    });

    canvas.addEventListener('pointermove', (e) => {
      const previous = down.get(e.pointerId);
      if (!previous) {
        // Pointeur relevé : simple survol. Seul le DOIGT est exclu -- il n'a pas de survol, et
        // laisser passer son pointermove poserait une étiquette fantôme sous le dernier appui.
        // Tout le reste (souris, stylet, et les évènements synthétiques au `pointerType` vide
        // que dispatchent les tests) survole normalement.
        if (e.pointerType !== 'touch') {
          this.lastPointerClient = { x: e.clientX, y: e.clientY };
          const { x, y } = ndcFrom(e.clientX, e.clientY);
          this.view?.handlePointerMove(x, y);
        }
        return;
      }
      down.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (down.size >= 2) {
        const span = pinchSpan();
        if (span && pinchPx > 0 && span.distance > 0) {
          const centre = ndcFrom(span.cx, span.cy);
          this.view?.pinchZoom(span.distance / pinchPx, centre.x, centre.y, this.aspect());
        }
        if (span) pinchPx = span.distance;
        return;
      }

      travelPx += Math.hypot(e.clientX - previous.x, e.clientY - previous.y);
      if (travelPx <= TAP_SLOP_PX) return;
      this.view?.clearHover();
      const from = ndcFrom(previous.x, previous.y);
      const to = ndcFrom(e.clientX, e.clientY);
      this.view?.dragGround(from.x, from.y, to.x, to.y);
    });

    const release = (e: PointerEvent, cancelled: boolean): void => {
      if (!down.has(e.pointerId)) return;
      down.delete(e.pointerId);
      if (down.size > 0) {
        // Un doigt sur deux relevé : on ne reprend pas un panoramique avec celui qui reste,
        // le geste garde son verdict jusqu'au bout.
        pinchPx = 0;
        return;
      }
      const wasTap = !cancelled && travelPx <= TAP_SLOP_PX;
      pinchPx = 0;
      travelPx = 0;
      if (!wasTap) {
        this.view?.clearHover();
        return;
      }
      const { x, y } = ndcFrom(e.clientX, e.clientY);
      // A tap has no previous hover; anchor its HTML label at the actual contact point.
      this.lastPointerClient = { x: e.clientX, y: e.clientY };
      this.view?.handlePointerMove(x, y);
      this.view?.handleClick(x, y);
    };
    canvas.addEventListener('pointerup', (e) => release(e, false));
    canvas.addEventListener('pointercancel', (e) => release(e, true));
    canvas.addEventListener('pointerleave', (e) => {
      // Touch pointers leave automatically after release; keep the tap label visible.
      if (e.pointerType !== 'touch' && down.size === 0) this.view?.clearHover();
    });

    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const { x, y } = ndcFrom(e.clientX, e.clientY);
        this.view?.zoomAtCursor(x, y, e.deltaY, this.aspect());
      },
      { passive: false },
    );
    window.addEventListener('resize', this.onResize);
  }

  private handleHover(target: HoverTarget | null): void {
    this.hoveredCharacterName = target?.type === 'character' ? target.name : null;
    if (target?.type === 'entity') {
      this.hoveredEntityId = target.id;
      const info = this.state?.listInteractables().find((i) => i.id === target.id);
      this.hud?.setHoverLabel(info?.label ?? target.id, info?.reachable ?? true, this.lastPointerClient);
    } else if (target?.type === 'character') {
      this.hoveredEntityId = null;
      this.hud?.setHoverLabel(target.name, true, this.lastPointerClient);
    } else {
      this.hoveredEntityId = null;
      this.hud?.setHoverLabel(null);
    }
  }

  /** Clic (ou selection clavier) sur un interactable : marche jusqu'a la case d'interaction, puis declenche. */
  private requestInteract(entityId: string): void {
    const res = this.state?.requestInteract(entityId);
    if (res && !res.ok)
      console.warn(`ChapterApp (exploration) : interaction refusee (${entityId}) : ${res.reason}`);
  }

  /**
   * Boucle d'image de l'exploration : `ExploreState.tick(dtMs)` (dtMs MESURE, jamais lu de
   * l'horloge par `tick` lui-meme -- ADR 0013 §3) puis rendu. Jeton de generation
   * (`loopId`) : si un evenement traite en cours de frame fait quitter l'exploration
   * (relaye a `ChapterApp` via `callbacks.onEvent`, qui peut en retour appeler `pause()` --
   * `completeExploreScene` -> `setActiveHost('dialogue')` -> `pause()` -> `stopLoop()`), la
   * frame s'arrete net plutot que de continuer a animer une scene qu'on vient de quitter,
   * et surtout sans planifier de frame suivante par-dessus celle qu'un `resume()` ulterieur
   * aurait deja replanifiee (cas d'ecole : deux scenes `explore` qui se suivent -- n'arrive
   * pas au chapitre 1, mais reste possible au lot 3.7).
   */
  private startLoop(): void {
    this.loopId += 1;
    const loopId = this.loopId;
    this.lastFrameTime = performance.now();

    const frame = (now: number): void => {
      if (loopId !== this.loopId) return;
      const state = this.state;
      const view = this.view;
      const renderer = this.renderer;
      if (!state || !view || !renderer) return;

      const dtMs = Math.min(now - this.lastFrameTime, 250);
      this.lastFrameTime = now;
      const dt = dtMs / 1000;

      const liveCtx = this.callbacks.getContext();
      state.updateContext(liveCtx);
      this.hud?.setGauge(this.gaugeStatusFor(liveCtx));
      for (const ev of state.tick(dtMs)) {
        this.callbacks.onEvent(ev);
        if (loopId !== this.loopId) return;
      }
      this.updateAudioLocation();

      view.panScreenRelative(this.heldKeys, dt);
      if (this.heldKeys.zoomIn) view.zoomBy(-KEY_ZOOM_SPEED * dt, this.aspect());
      if (this.heldKeys.zoomOut) view.zoomBy(KEY_ZOOM_SPEED * dt, this.aspect());

      view.updateRigPosition('leader', state.leaderPosition(), state.isMoving(), dt);
      const positions = state.followerPositions();
      this.followerRigIds.forEach((id, i) => {
        const pos = positions[i];
        if (pos) view.updateRigPosition(id, pos, state.isMoving(), dt);
      });
      view.tick(dt);

      this.briefLine?.tick(dt);
      const trackedEntityId = this.briefLine?.entityToTrack;
      if (trackedEntityId) {
        const cell = this.entity(trackedEntityId)?.cell;
        const pos = cell ? view.projectToScreen(cell, this.host.clientWidth, this.host.clientHeight) : null;
        this.briefLine?.setSpeechScreenPosition(pos);
      }

      const interactables = state.listInteractables();
      this.hud?.setInteractables(
        interactables.map((it) => ({ id: it.id, label: it.label, reachable: it.reachable })),
      );
      this.syncVisibility(interactables);
      this.stableLights?.update(view.camera.getTarget());
      if (this.hoveredEntityId) {
        const info = interactables.find((i) => i.id === this.hoveredEntityId);
        if (info) this.hud?.setHoverLabel(info.label, info.reachable, this.lastPointerClient);
      } else if (this.hoveredCharacterName) {
        this.hud?.setHoverLabel(this.hoveredCharacterName, true, this.lastPointerClient);
      }

      renderer.info.reset();
      if (view.isEnhancedExplorationRenderingActive && this.dormitoryComposer) {
        if (this.dormitoryRenderPass) this.dormitoryRenderPass.camera = view.renderCamera;
        const previousExposure = renderer.toneMappingExposure;
        renderer.toneMappingExposure = DORMITORY_PILOT_EXPOSURE;
        try {
          this.dormitoryComposer.render();
        } finally {
          renderer.toneMappingExposure = previousExposure;
        }
      } else {
        renderer.render(view.scene, view.renderCamera);
      }
      this.rafId = requestAnimationFrame(frame);
    };

    this.rafId = requestAnimationFrame(frame);
  }

  private stopLoop(): void {
    this.loopId += 1; // invalide toute frame deja planifiee
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private aspect(): number {
    return Math.max(0.1, this.host.clientWidth / Math.max(1, this.host.clientHeight));
  }

  /**
   * Pousse vers le rendu ce qui est actuellement visible (08-EXPLORATION.md "La découverte des
   * lieux") : entités npc/object/seat actives ET découvertes, pièces découvertes. `ExploreView`
   * ne fait qu'obéir -- la règle vit entièrement dans `ExploreState` (pur, testable sans
   * navigateur). Appelée une fois à l'entrée d'une étape (cold start ET reprise sur la même
   * carte, où le contexte -- donc les entités actives -- peut changer sans reconstruire l'état)
   * et à chaque frame utile de la boucle (la découverte évolue en cours de partie).
   */
  private syncVisibility(interactables?: InteractableInfo[]): void {
    if (!this.state || !this.view) return;
    const list = interactables ?? this.state.listInteractables();
    const entityIds = list
      .filter((i) => i.type === 'npc' || i.type === 'object' || i.type === 'seat')
      .map((i) => i.id);
    this.view.setVisibleEntities(entityIds);
    this.view.setActiveDoors(list.filter((i) => i.type === 'door').map((i) => i.id));
    this.view.setDiscoveredRooms(this.state.discoveredRoomIds());
    const trigger = this.objectiveTriggerId;
    // Une porte/zone n'est pas dans `entityIds` (elles ne sont pas des "contenus" de piece) mais
    // reste visible des que sa piece l'est : la balise suit donc la presence dans `list`, seule
    // source qui tienne compte a la fois de la condition d'etape et de la decouverte.
    const target =
      trigger && list.some((i) => i.id === trigger) ? (this.entity(trigger)?.cell ?? null) : null;
    this.view.setObjectiveTarget(target);
  }

  /** Ajoute/retire les rigs des coequipiers pour correspondre exactement a `ids` (ordre du roster). */
  private syncFollowerRigs(ids: FollowerId[]): void {
    if (!this.view) return;
    for (const id of this.followerRigIds) {
      if (!ids.includes(id as FollowerId)) this.view.removeRig(id);
    }
    for (const id of ids) {
      if (this.followerRigIds.includes(id)) continue;
      // L'enfant (ADR 0024 §3) n'a pas de `CharacterSheet` -- silhouette dediee.
      if (id === 'enfant') this.view.setChildFollower(id);
      else this.view.setFollower(id, getCharacter(id));
    }
    this.followerRigIds = [...ids];
  }

  /* ---------------------------------- interface pour le routage de ChapterApp ---------------------------------- */

  /**
   * Definition de l'entite `entityId` sur la carte courante, si elle existe -- utilise par
   * `ChapterApp` pour router une interaction (case a viser, ligne de repli d'une
   * conversation deja jouee...) sans dupliquer la recherche.
   */
  entity(entityId: string): EntityDef | undefined {
    return this.mapDef?.entities.find((e) => e.id === entityId);
  }

  /** `npc` : bulle parlee (suit la tete). `object`/`door` : narration discrete en bas de l'ecran. */
  playBriefLine(entityId: string, text: string): void {
    if (!this.briefLine) return;
    const entity = this.entity(entityId);
    if (entity?.type === 'npc') this.briefLine.showSpeech(entityId, text);
    else this.briefLine.showNarration(text);
  }

  /** Bruitage(s) d'une replique radio/pression echue (ADR 0024 §2, `RadioCue.sfx`). */
  playSfx(ids: readonly SfxId[]): void {
    for (const id of ids) this.sfx.play(id);
  }

  setDoorOpen(entityId: string, open: boolean): void {
    this.view?.setDoorOpen(entityId, open);
  }

  /**
   * Force `entityId` (une porte verrouillee) a l'etat ouvert, cote etat ET rendu -- voir
   * `ChapterApp.unlockDoorIfNeeded` pour le contexte complet (lot 3.7b).
   */
  unlockDoor(entityId: string): void {
    this.state?.forceDoorOpen(entityId);
    this.view?.setDoorOpen(entityId, true);
  }

  faceLeaderTowards(cell: Cell): void {
    this.view?.faceLeaderTowards(cell);
  }

  refreshObjectiveHud(): void {
    this.hud?.setObjective(this.state?.objectiveStatus() ?? null);
  }
}
